/**
 * Always-on Live: persist preference, screen wake lock, Windows startup helper.
 */

const KEY = "visionforge.alwaysOn.v1";
const PROD_ORIGIN = "https://visionforge.forgesuiteui.app";

export function loadAlwaysOn(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function saveAlwaysOn(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/** Consume ?alwaysOn=1 from a login / startup handoff. */
export function consumeAlwaysOnQuery(): boolean {
  try {
    const sp = new URLSearchParams(window.location.search);
    const raw = sp.get("alwaysOn") || sp.get("alwayson");
    if (raw !== "1" && raw !== "true") return loadAlwaysOn();
    saveAlwaysOn(true);
    const url = new URL(window.location.href);
    url.searchParams.delete("alwaysOn");
    url.searchParams.delete("alwayson");
    history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    return true;
  } catch {
    return loadAlwaysOn();
  }
}

type WakeSentinel = { released: boolean; release: () => Promise<void> };

let sentinel: WakeSentinel | null = null;

export async function acquireWakeLock(): Promise<boolean> {
  const nav = navigator as Navigator & {
    wakeLock?: { request: (type: "screen") => Promise<WakeSentinel> };
  };
  if (!nav.wakeLock?.request) return false;
  try {
    sentinel = await nav.wakeLock.request("screen");
    return true;
  } catch {
    return false;
  }
}

export async function releaseWakeLock(): Promise<void> {
  try {
    await sentinel?.release();
  } catch {
    /* ignore */
  }
  sentinel = null;
}

function vbsEscape(s: string): string {
  return s.replace(/"/g, '""');
}

/** VBScript that copies itself into the Windows Startup folder and opens Live. */
export function alwaysOnStartupVbs(origin: string): string {
  const url = `${origin.replace(/\/$/, "")}/?alwaysOn=1`;
  const u = vbsEscape(url);
  return `' VisionForge Always On
' Double-click once: copies this file into your Startup folder, then opens Live.
Option Explicit
Dim sh, fso, startup, dest, url, cmd
Set sh = CreateObject("WScript.Shell")
Set fso = CreateObject("Scripting.FileSystemObject")
startup = sh.SpecialFolders("Startup")
dest = startup & "\\VisionForge Always On.vbs"
url = "${u}"
If StrComp(WScript.ScriptFullName, dest, vbTextCompare) <> 0 Then
  fso.CopyFile WScript.ScriptFullName, dest, True
End If
On Error Resume Next
cmd = "msedge --app=""" & url & """"
sh.Run cmd, 1, False
If Err.Number <> 0 Then
  Err.Clear
  sh.Run "chrome --app=""" & url & """", 1, False
End If
If Err.Number <> 0 Then
  Err.Clear
  sh.Run "cmd /c start """" """ & url & """", 0, False
End If
`;
}

export function alwaysOnUninstallVbs(): string {
  return `' Remove VisionForge Always On from Windows Startup
Option Explicit
Dim sh, fso, dest
Set sh = CreateObject("WScript.Shell")
Set fso = CreateObject("Scripting.FileSystemObject")
dest = sh.SpecialFolders("Startup") & "\\VisionForge Always On.vbs"
If fso.FileExists(dest) Then fso.DeleteFile dest, True
`;
}

export function downloadTextFile(filename: string, body: string, mime: string): void {
  const blob = new Blob([body], { type: mime });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export function downloadAlwaysOnInstaller(useLocalOrigin: boolean): void {
  const origin = useLocalOrigin ? window.location.origin : PROD_ORIGIN;
  downloadTextFile(
    "VisionForge-AlwaysOn.vbs",
    alwaysOnStartupVbs(origin),
    "text/vbscript",
  );
}

export function downloadAlwaysOnUninstaller(): void {
  downloadTextFile(
    "VisionForge-RemoveAlwaysOn.vbs",
    alwaysOnUninstallVbs(),
    "text/vbscript",
  );
}

export { PROD_ORIGIN };
